import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import clsx from "clsx";

const BACKEND_URL = import.meta.env.VITE_API_URL?.replace("/api", "") || "http://localhost:4000";

/* ─── Role colors ────────────────────────────────────────────────────────── */
const ROLE_COLORS = {
  student: { bg: "rgba(139,92,246,0.15)", color: "#8b5cf6", label: "Schüler" },
  teacher: { bg: "rgba(6,182,212,0.15)",  color: "#06b6d4", label: "Lehrer" },
  mentor:  { bg: "rgba(34,197,94,0.15)",  color: "#22c55e", label: "Mentor" },
  parent:  { bg: "rgba(251,191,36,0.15)", color: "#fbbf24", label: "Elternteil" },
};

function UserAvatar({ user, size = 40 }) {
  const bgColor = ROLE_COLORS[user.role]?.color || "#8b5cf6";
  return (
    <div
      className="flex-shrink-0 rounded-xl overflow-hidden flex items-center justify-center"
      style={{ width: size, height: size, background: `${bgColor}20`, border: `1.5px solid ${bgColor}40` }}
    >
      {user.avatarUrl ? (
        <img src={`${BACKEND_URL}${user.avatarUrl}`} alt={user.name}
          className="w-full h-full object-cover" />
      ) : (
        <span style={{ fontSize: size * 0.45 }}>{user.avatar || "🦊"}</span>
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  const r = ROLE_COLORS[role] || ROLE_COLORS.student;
  return (
    <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md"
      style={{ color: r.color, background: r.bg, border: `1px solid ${r.color}30` }}>
      {r.label}
    </span>
  );
}

export default function Friends() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState("friends"); // friends | requests | search
  const [searchQ, setSearchQ] = useState("");
  const [reportTarget, setReportTarget] = useState(null);
  const [reportType, setReportType] = useState("other");
  const [reportDesc, setReportDesc] = useState("");

  /* ── Queries ────────────────────────────────────────────────────────── */
  const { data: friendsData } = useQuery({
    queryKey: ["friends"],
    queryFn: () => api.get("/social/friends").then((r) => r.data),
  });

  const { data: requestsData } = useQuery({
    queryKey: ["friend-requests"],
    queryFn: () => api.get("/social/friends/requests").then((r) => r.data),
    refetchInterval: 20_000,
  });

  const { data: searchData } = useQuery({
    queryKey: ["user-search", searchQ],
    queryFn: () => searchQ.length >= 2 ? api.get(`/social/search?q=${encodeURIComponent(searchQ)}`).then((r) => r.data) : { users: [] },
    enabled: searchQ.length >= 2,
  });

  const { data: blockData } = useQuery({
    queryKey: ["blocked"],
    queryFn: () => api.get("/social/block").then((r) => r.data),
  });

  /* ── Mutations ──────────────────────────────────────────────────────── */
  const sendRequest = useMutation({
    mutationFn: (toUserId) => api.post("/social/friends/send", { toUserId }),
    onSuccess: () => { toast.success("Freundschaftsanfrage gesendet! 🤝"); qc.invalidateQueries({ queryKey: ["friend-requests"] }); },
    onError: (e) => toast.error(e.response?.data?.error || "Fehler"),
  });

  const respond = useMutation({
    mutationFn: ({ friendshipId, action }) => api.post("/social/friends/respond", { friendshipId, action }),
    onSuccess: (_, v) => {
      toast.success(v.action === "accept" ? "Freundschaft angenommen! 🎉" : "Anfrage abgelehnt");
      qc.invalidateQueries({ queryKey: ["friend-requests"] });
      qc.invalidateQueries({ queryKey: ["friends"] });
    },
    onError: (e) => toast.error(e.response?.data?.error || "Fehler"),
  });

  const unfriend = useMutation({
    mutationFn: (userId) => api.delete(`/social/friends/${userId}`),
    onSuccess: () => { toast.success("Freundschaft beendet"); qc.invalidateQueries({ queryKey: ["friends"] }); },
  });

  const blockUser = useMutation({
    mutationFn: (userId) => api.post("/social/block", { userId }),
    onSuccess: () => {
      toast.success("Nutzer blockiert");
      qc.invalidateQueries({ queryKey: ["friends"] });
      qc.invalidateQueries({ queryKey: ["blocked"] });
    },
  });

  const unblockUser = useMutation({
    mutationFn: (userId) => api.delete(`/social/block/${userId}`),
    onSuccess: () => { toast.success("Blockierung aufgehoben"); qc.invalidateQueries({ queryKey: ["blocked"] }); },
  });

  const submitReport = useMutation({
    mutationFn: (data) => api.post("/social/report", data),
    onSuccess: () => {
      toast.success("Meldung eingereicht. Das Admin-Team prüft den Fall.");
      setReportTarget(null);
      setReportDesc("");
    },
    onError: (e) => toast.error(e.response?.data?.error || "Fehler"),
  });

  const friends   = friendsData?.friends || [];
  const incoming  = requestsData?.incoming || [];
  const outgoing  = requestsData?.outgoing || [];
  const results   = searchData?.users || [];
  const blocked   = blockData?.blocks || [];

  const friendIds = new Set(friends.map((f) => f.friend.id));
  const outgoingIds = new Set(outgoing.map((r) => r.toUser?.id).filter(Boolean));

  const pendingCount = incoming.length;

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-black flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          Freunde
        </h1>
        <span className="text-xs text-gray-500 font-bold">{friends.length} Freunde</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5">
        {[
          { id: "friends",  label: "Freunde" },
          { id: "requests", label: "Anfragen", badge: pendingCount },
          { id: "search",   label: "Suchen" },
          { id: "blocked",  label: "Blockiert" },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={clsx(
              "relative flex-1 py-2 rounded-xl text-xs font-bold border transition-all",
              tab === t.id
                ? "bg-violet-600 border-violet-500 text-white shadow-flow"
                : "bg-nexus-s2 border-nexus-border text-gray-400 hover:border-violet-500/30"
            )}>
            {t.label}
            {t.badge > 0 && (
              <span className="absolute -top-1.5 -right-1 w-4 h-4 rounded-full bg-pink-500 text-white text-[9px] font-black flex items-center justify-center">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Friends list ────────────────────────────────────────────────── */}
      {tab === "friends" && (
        friends.length === 0 ? (
          <div className="text-center py-12 text-gray-600">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" stroke="#374151" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <p className="font-bold text-sm">Noch keine Freunde</p>
            <p className="text-xs mt-1">Suche nach Mitschülern und adde sie!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {friends.map(({ friendshipId, friend, since }) => (
              <div key={friendshipId}
                className="flex items-center gap-3 p-3.5 rounded-2xl border border-nexus-border bg-nexus-sf">
                <UserAvatar user={friend} size={44} />
                <div className="flex-1 min-w-0">
                  <p className="font-black text-sm truncate">{friend.name}</p>
                  <RoleBadge role={friend.role} />
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => navigate(`/messages?new=${friend.id}`)}
                    className="px-2.5 py-1.5 rounded-xl bg-violet-600/20 border border-violet-500/30 text-violet-400 text-xs font-bold hover:bg-violet-600/30 transition-colors">
                    Chat
                  </button>
                  <button
                    onClick={() => { if (confirm(`${friend.name} entfreunden?`)) unfriend.mutate(friend.id); }}
                    className="px-2 py-1.5 rounded-xl bg-nexus-s2 border border-nexus-border text-gray-500 text-xs hover:text-red-400 hover:border-red-900/40 transition-colors">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => setReportTarget(friend)}
                    className="px-2 py-1.5 rounded-xl bg-nexus-s2 border border-nexus-border text-gray-500 text-xs hover:text-orange-400 hover:border-orange-900/40 transition-colors">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ── Requests ────────────────────────────────────────────────────── */}
      {tab === "requests" && (
        <div className="space-y-4">
          {/* Incoming */}
          {incoming.length > 0 && (
            <div>
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">
                Eingehend ({incoming.length})
              </p>
              <div className="space-y-2">
                {incoming.map((req) => (
                  <div key={req.id}
                    className="flex items-center gap-3 p-3.5 rounded-2xl border border-violet-700/30 bg-violet-950/10">
                    <UserAvatar user={req.fromUser} size={44} />
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-sm truncate">{req.fromUser.name}</p>
                      <RoleBadge role={req.fromUser.role} />
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => respond.mutate({ friendshipId: req.id, action: "accept" })}
                        className="px-3 py-1.5 rounded-xl bg-green-600 text-white text-xs font-black hover:bg-green-500 transition-colors">
                        ✓
                      </button>
                      <button
                        onClick={() => respond.mutate({ friendshipId: req.id, action: "decline" })}
                        className="px-3 py-1.5 rounded-xl bg-nexus-s2 border border-nexus-border text-gray-400 text-xs font-black hover:border-red-900/40 hover:text-red-400 transition-colors">
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Outgoing */}
          {outgoing.length > 0 && (
            <div>
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">
                Ausstehend ({outgoing.length})
              </p>
              <div className="space-y-2">
                {outgoing.map((req) => (
                  <div key={req.id}
                    className="flex items-center gap-3 p-3.5 rounded-2xl border border-nexus-border bg-nexus-sf opacity-70">
                    <UserAvatar user={req.toUser} size={44} />
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-sm truncate">{req.toUser?.name}</p>
                      <RoleBadge role={req.toUser?.role} />
                    </div>
                    <span className="text-[10px] text-gray-500 font-bold">Ausstehend…</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {incoming.length === 0 && outgoing.length === 0 && (
            <div className="text-center py-12 text-gray-600">
              <p className="font-bold text-sm">Keine Anfragen</p>
              <p className="text-xs mt-1">Suche nach Mitschülern um Freundschaften zu beginnen</p>
            </div>
          )}
        </div>
      )}

      {/* ── Search ──────────────────────────────────────────────────────── */}
      {tab === "search" && (
        <div className="space-y-3">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#4b5563" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Namen eingeben…"
              className="w-full pl-9 pr-4 py-2.5 bg-nexus-s2 border border-nexus-border rounded-xl text-sm text-white placeholder-gray-600 focus:border-violet-500/50 focus:outline-none transition-colors"
            />
          </div>

          {results.length > 0 && (
            <div className="space-y-2">
              {results.map((u) => {
                const isFriend = friendIds.has(u.id);
                const isPending = outgoingIds.has(u.id);
                return (
                  <div key={u.id}
                    className="flex items-center gap-3 p-3.5 rounded-2xl border border-nexus-border bg-nexus-sf">
                    <UserAvatar user={u} size={44} />
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-sm truncate">{u.name}</p>
                      <RoleBadge role={u.role} />
                    </div>
                    {isFriend ? (
                      <span className="text-[10px] text-green-400 font-black">✓ Freund</span>
                    ) : isPending ? (
                      <span className="text-[10px] text-gray-500 font-bold">Ausstehend</span>
                    ) : (
                      <button
                        onClick={() => sendRequest.mutate(u.id)}
                        disabled={sendRequest.isPending}
                        className="px-3 py-1.5 rounded-xl bg-violet-600 text-white text-xs font-black hover:bg-violet-500 transition-colors disabled:opacity-50">
                        + Adden
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {searchQ.length >= 2 && results.length === 0 && (
            <p className="text-center text-xs text-gray-600 py-6">Keine Ergebnisse für „{searchQ}"</p>
          )}

          {searchQ.length < 2 && (
            <p className="text-center text-xs text-gray-600 py-6">Mindestens 2 Zeichen eingeben</p>
          )}
        </div>
      )}

      {/* ── Blocked ─────────────────────────────────────────────────────── */}
      {tab === "blocked" && (
        blocked.length === 0 ? (
          <div className="text-center py-10 text-gray-600">
            <p className="font-bold text-sm">Niemand blockiert</p>
          </div>
        ) : (
          <div className="space-y-2">
            {blocked.map(({ id, blocked: u }) => (
              <div key={id} className="flex items-center gap-3 p-3.5 rounded-2xl border border-nexus-border bg-nexus-sf opacity-70">
                <UserAvatar user={u} size={44} />
                <div className="flex-1 min-w-0">
                  <p className="font-black text-sm truncate">{u.name}</p>
                </div>
                <button onClick={() => unblockUser.mutate(u.id)}
                  className="px-3 py-1.5 rounded-xl bg-nexus-s2 border border-nexus-border text-gray-400 text-xs font-bold hover:border-green-900/40 hover:text-green-400 transition-colors">
                  Freigeben
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {/* ── Report modal ────────────────────────────────────────────────── */}
      {reportTarget && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setReportTarget(null)}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"/>
          <div className="relative z-10 w-full max-w-sm bg-nexus-bg border border-nexus-border rounded-3xl overflow-hidden shadow-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-sm flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#f97316" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                Nutzer melden
              </h3>
              <button onClick={() => setReportTarget(null)} className="w-7 h-7 rounded-lg bg-nexus-s2 flex items-center justify-center text-gray-500 hover:text-white transition-colors">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <p className="text-xs text-gray-400">Meldung gegen <strong className="text-white">{reportTarget.name}</strong></p>
            <div>
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-1.5 block">Grund</label>
              <select value={reportType} onChange={(e) => setReportType(e.target.value)}
                className="w-full bg-nexus-s2 border border-nexus-border rounded-xl px-3 py-2 text-sm text-white focus:border-violet-500/50 focus:outline-none">
                <option value="chat_abuse">Chat-Missbrauch</option>
                <option value="bullying">Mobbing</option>
                <option value="violence">Gewalt / Bedrohung</option>
                <option value="inappropriate">Unangemessene Inhalte</option>
                <option value="other">Sonstiges</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-wide mb-1.5 block">Beschreibung</label>
              <textarea value={reportDesc} onChange={(e) => setReportDesc(e.target.value)}
                rows={3} placeholder="Was ist passiert?"
                className="w-full bg-nexus-s2 border border-nexus-border rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-violet-500/50 focus:outline-none resize-none" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setReportTarget(null)}
                className="flex-1 py-2.5 rounded-xl bg-nexus-s2 border border-nexus-border text-gray-400 text-sm font-bold hover:border-gray-600 transition-colors">
                Abbrechen
              </button>
              <button
                onClick={() => submitReport.mutate({ targetUserId: reportTarget.id, type: reportType, description: reportDesc })}
                disabled={!reportDesc.trim() || submitReport.isPending}
                className="flex-1 py-2.5 rounded-xl bg-orange-600 text-white text-sm font-black hover:bg-orange-500 transition-colors disabled:opacity-50">
                Melden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
